/**
 * =============================================================================
 * SETERA-WEB
 * =============================================================================
 * Copyright (c) 2024 HG Digital/Setera
 * Todos os direitos reservados.
 * 
 * Este software é confidencial e de propriedade da HG Digital/Setera.
 * Seu uso está sujeito aos termos do contrato de licença.
 * -----
 * Criado em : 21-12-2024
 * Autor     : Sistema
 * Projeto   : SETERA-WEB
 * Versão    : 1.0.0
 * =============================================================================
 */

/**
 * Utilitário para tratamento de erros específicos de funcionalidade
 * 
 * Extrai mensagens de erro da resposta da API de forma inteligente,
 * priorizando mensagens localizadas quando disponíveis
 */

/**
 * Extrai a mensagem de erro mais apropriada da resposta da API
 * 
 * @param error Objeto de erro retornado pela API
 * @param fallbackMessage Mensagem padrão caso não consiga extrair erro específico
 * @returns Mensagem de erro para exibir ao usuário
 */
export function extractFunctionalityErrorMessage(error: any, fallbackMessage: string): string {
  try {
    // Primeiro, tenta acessar a resposta da API
    const responseData = error?.response?.data;
    
    if (responseData) {
      // Prioriza mensagem localizada (validações de negócio)
      if (responseData.localized_message) {
        return responseData.localized_message;
      }
      
      // Fallback para mensagem padrão da API
      if (responseData.message) {
        return responseData.message;
      }
      
      // Se for array de erros, pega o primeiro
      if (Array.isArray(responseData) && responseData.length > 0 && responseData[0].message) {
        return responseData[0].message;
      }
    }
    
    // Se o erro tem uma mensagem direta
    if (error?.message) {
      return error.message;
    }
    
    // Último recurso - mensagem padrão
    return fallbackMessage;
    
  } catch (extractionError) {
    // Se algo der errado na extração, retorna mensagem padrão
    console.error('Erro ao extrair mensagem de erro:', extractionError);
    return fallbackMessage;
  }
}

/**
 * Verifica se é um erro de validação de negócio (400)
 * 
 * @param error Objeto de erro
 * @returns true se for erro de validação
 */
export function isValidationError(error: any): boolean {
  return error?.response?.status === 400;
}

/**
 * Verifica se é um erro específico de nome duplicado
 * 
 * @param error Objeto de erro
 * @returns true se for erro de nome duplicado
 */
export function isDuplicateNameError(error: any): boolean {
  const message = extractFunctionalityErrorMessage(error, '');
  return message.includes('nome') && message.includes('já existe');
}

/**
 * Verifica se é um erro específico de código duplicado
 * 
 * @param error Objeto de erro
 * @returns true se for erro de código duplicado
 */
export function isDuplicateCodeError(error: any): boolean {
  const message = extractFunctionalityErrorMessage(error, '');
  return message.includes('código') && message.includes('já existe');
}

/**
 * Mensagens de erro padronizadas para funcionalidade
 */
export const FUNCTIONALITY_ERROR_MESSAGES = {
  CREATE_GENERIC: 'Ocorreu um erro ao salvar a funcionalidade. Tente novamente.',
  UPDATE_GENERIC: 'Ocorreu um erro ao atualizar a funcionalidade. Tente novamente.',
  CLONE_GENERIC: 'Ocorreu um erro ao duplicar a funcionalidade. Tente novamente.',
  NETWORK_ERROR: 'Erro de conexão. Verifique sua internet e tente novamente.',
  SERVER_ERROR: 'Erro interno do servidor. Tente novamente em alguns instantes.'
} as const; 